/**
 * localStorage utilities with token management.
 *
 * SECURITY NOTE: JWT tokens are currently stored in localStorage.
 * This is vulnerable to XSS attacks - any injected script can read tokens.
 *
 * TODO: Migrate to httpOnly cookies for token storage (requires backend changes):
 *   1. Backend sets httpOnly, Secure, SameSite=Strict cookies on login
 *   2. Frontend removes token from localStorage
 *   3. Axios sends credentials automatically via withCredentials: true
 *
 * Current mitigations:
 *   - All user-generated HTML is sanitized via DOMPurify before rendering
 *   - Content Security Policy headers should be configured on the server
 *   - Tokens have short expiry (access token) with refresh token rotation
 */

export const getItem = <T>(key: string): T | null => {
  try {
    const item = window.localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : null;
  } catch (error) {
    console.error(`Error reading from localStorage key "${key}":`, error);
    return null;
  }
};

export const setItem = <T>(key: string, value: T): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error writing to localStorage key "${key}":`, error);
  }
};

export const removeItem = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
  }
};

// Token 专用封装
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

export const getToken = (): string | null => {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const saveToken = (token: string): void => {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Error saving token:', error);
  }
};

export const removeToken = (): void => {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error('Error removing token:', error);
  }
};

export const getRefreshToken = (): string | null => {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const saveRefreshToken = (token: string): void => {
  try {
    window.localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error('Error saving refresh token:', error);
  }
};

export const removeRefreshToken = (): void => {
  try {
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Error removing refresh token:', error);
  }
};

export const getStoredUser = <T>(): T | null => {
  return getItem<T>(USER_KEY);
};

export const saveUser = <T>(user: T): void => {
  setItem(USER_KEY, user);
};

export const removeUser = (): void => {
  removeItem(USER_KEY);
};

/** Clear all authentication data - use on logout or suspected compromise */
export const clearAllAuth = (): void => {
  removeToken();
  removeRefreshToken();
  removeUser();
};
